import { useTranslations } from '@aikoa/i18n/react'
import Animated, {
  Extrapolation,
  FadeInDown,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated'
import { useScrollContext } from '~/components/layout/ScrollContext'
import { Button, ButtonLabel, type ButtonVariant } from '~/components/ui/button'
import { cn } from '~/lib/utils.ts'

export default function FlowNavigation({
  onPrevious,
  onNext,
  className,
  nextLabel,
  previousLabel,
  nextDisabled = false,
  nextButtonVariant = 'primary',
  previousDisabled = false,
  isLoading = false,
  showBackground = true,
  nextTestId,
  previousTestId,
}: {
  onPrevious?: () => void
  onNext?: () => void
  className?: string
  nextLabel?: string
  previousLabel?: string
  nextDisabled?: boolean
  nextButtonVariant?: ButtonVariant
  previousDisabled?: boolean
  isLoading?: boolean
  showBackground?: boolean
  nextTestId?: string
  previousTestId?: string
}) {
  const { t } = useTranslations('common')
  const defaultNextLabel = nextLabel ?? t(($) => $.navigation.next)
  const defaultPreviousLabel = previousLabel ?? t(($) => $.navigation.previous)

  const { scrollY, maxScrollY } = useScrollContext()

  const clampedProgress = useDerivedValue(() =>
    interpolate(scrollY.value, [0, maxScrollY], [0, 1], Extrapolation.CLAMP)
  )

  const buttonStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: interpolate(clampedProgress.value, [0, 1], [0.925, 1]),
        },
      ],
      opacity: nextDisabled ? interpolate(clampedProgress.value, [0, 1], [0.5, 0.8]) : 1,
    }
  })

  const buttonBarStyle = useAnimatedStyle(() => {
    if (!showBackground) {
      return {
        backgroundColor: 'transparent',
        // eslint-disable-next-line i18next/no-literal-string
        borderTopColor: 'transparent',
      }
    }

    const progress = clampedProgress.value

    return {
      backgroundColor: `rgba(245, 245, 255, ${1 - progress})`,
      borderTopColor: `rgba(240, 240, 250, ${1 - progress})`,
    }
  })

  return (
    <Animated.View
      entering={FadeInDown.duration(150).delay(150)}
      style={[buttonBarStyle]}
      className={cn(
        'native:absolute pointer-events-none bottom-0 z-20 w-full flex-row-reverse items-start justify-between gap-6 rounded-b-content border-t px-4 pb-3 pt-2.5 web:sticky',
        !onPrevious && 'justify-center',
        className
      )}>
      {!!onNext && (
        <Button
          testID={nextTestId ?? 'flow-next-button'}
          style={buttonStyle}
          onPress={onNext}
          className="pointer-events-auto relative z-10"
          variant={nextButtonVariant ?? 'primary'}
          size="lg"
          loading={isLoading}
          disabled={nextDisabled || isLoading}>
          <ButtonLabel>{defaultNextLabel}</ButtonLabel>
        </Button>
      )}
      {!!onPrevious && (
        <Button
          testID={previousTestId ?? 'flow-previous-button'}
          style={buttonStyle}
          variant="ghost"
          onPress={onPrevious}
          className="pointer-events-auto relative z-10"
          size="lg"
          disabled={previousDisabled || isLoading}>
          {defaultPreviousLabel}
        </Button>
      )}
    </Animated.View>
  )
}
